<template>
	<div class="container mt-4">
		<div class="justify-content-center">
			<ReviewCard class="m-3" v-if="review" :review="review" />

			<template v-if="surveyResponse">
				<button class="btn btn-link text-primary font-weight-bold w-100 text-left border mb-2" type="button"
					data-toggle="collapse" data-target="#surveyCardCollapse"
					aria-controls="surveyCardCollapse">
					{{ __('Show Survey Response') }}
				</button>

				<!-- Collapsible Review Card -->
				<div class="collapse" id="surveyCardCollapse">
					<SurveysSingleResponse :response="surveyResponse" :action-buttons="false" />
				</div>

			</template>
		</div>
		<div class="row justify-content-center">
			<!-- Review Card -->
			<div class="col-sm-12 col-md-8 col-lg-6">
				<form @submit.prevent="submitForm" class="overflow-auto">
					<!-- Type Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.type }">
						<label for="type">{{ __('Type') }}</label>
						<select id="type" class="form-control" v-model="form.type" required>
							<option value="task">{{ __('Task') }}</option>
							<option value="issue">{{ __('Issue') }}</option>
							<option value="request">{{ __('Request') }}</option>
						</select>
						<div v-if="formErrors.type" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.type[0] }}
						</div>
					</div>

					<!-- Title Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.title }">
						<label for="title">{{ __('Title') }}</label>
						<input type="text" id="title" class="form-control" v-model="form.title" required
							:placeholder="__('Enter a title')" />
						<div v-if="formErrors.title" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.title[0] }}
						</div>
					</div>

					<!-- Description Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.description }">
						<label for="description">{{ __('Description') }}</label>
						<textarea id="description" class="form-control" v-model="form.description" required
							:placeholder="__('Enter a description')"></textarea>
						<div v-if="formErrors.description" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i>
							{{ formErrors.description[0] }}
						</div>
					</div>

					<!-- Assignees Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.assignees }">
						<label for="assignees">{{ __('Assignees') }}</label>
						<AssigneesSelector ref="assigneesSelector" id="assignees" v-model="form.assignees"
							:users="spark.state.currentTeam.users" :groups="groups" :allow-emails="true"
							:allow-groups="true" :allow-users="true" :placeholder="__('Search for assignees')"
							@message="setStatusMessage" />
						<div v-if="formErrors.assignees" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.assignees[0] }}
						</div>
					</div>

					<!-- Department Selector Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.departments }">
						<label for="departments">{{ __('Departments') }}</label>
						<DepartmentSelector ref="departmentSelector" v-model="form.departments"
							:departments="departments" :placeholder="__('Select departments')"
							@message="setStatusMessage" />
						<div v-if="formErrors.departments" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.departments[0] }}
						</div>
					</div>

					<!-- Priority Field -->
					<div class="form-group" :class="{ 'has-error': formErrors.priority }">
						<label for="priority">{{ __('Priority') }}</label>
						<select id="priority" class="form-control" v-model="form.priority" required>
							<option value="0">{{ __('Low') }}</option>
							<option value="1">{{ __('Normal') }}</option>
							<option value="2">{{ __('High') }}</option>
						</select>
						<div v-if="formErrors.priority" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.priority[0] }}
						</div>
					</div>

					<!-- Status Field -->
					<div v-if="caseId" class="form-group" :class="{ 'has-error': formErrors.status }">
						<label for="status">{{ __('Status') }}</label>
						<select id="status" class="form-control" v-model="form.status" required>
							<option value="new" disabled>{{ __('New') }}</option>
							<option value="open">{{ __('Open') }}</option>
							<option value="closed">{{ __('Closed') }}</option>
						</select>
						<div v-if="formErrors.status" class="text-danger d-block mt-1">
							<i class="fas fa-exclamation-circle"></i> {{ formErrors.status[0] }}
						</div>
					</div>

					<div class="form-group text-center">
						<button type="submit" class="btn btn-primary">
							{{ caseId ? __('Update') : __('Submit') }}
						</button>
					</div>

					<!-- Status Message -->
					<div v-if="statusMessage.message" :class="`alert alert-${statusMessage.type} mt-3`" role="alert">
						{{ statusMessage.message }}
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<script>
import AssigneesSelector from '@/components/cases/AssigneesSelector.vue';
import DepartmentSelector from '@/components/cases/DepartmentSelector.vue';
import CaseService from '@/services/cases-service';
import ReviewsService from '@/services/reviews-service';
import ReviewCard from '@/components/cases/ReviewCard.vue';
import SurveysService from '@/services/surveys-service';
import SurveysSingleResponse from '../surveys/SurveysSingleResponse.vue';
export default {
	components: {
		AssigneesSelector,
		ReviewCard,
		SurveysSingleResponse,
		DepartmentSelector,
	},
	data() {
		return {
			form: {
				type: 'task',
				title: '',
				description: '',
				assignees: [],
				departments: [], 
				priority: '1',
				status: 'new',
				review_id: null,
				survey_response_id: null,
			},
			formErrors: {},
			statusMessage: { message: '', type: 'success' },
			caseId: null,
			review: null,
			surveyResponse: null,
			groups: [],
			departments: [],
			defaults: {},
		};
	},

	mounted() {
		this.$eventHub.$on('activateTab', this.handleTabActivation);
	},

	beforeDestroy() {
		this.$eventHub.$off('activateTab', this.handleTabActivation);
	},

	methods: {
		async fetchSettings() {
			try {
				const { groups, defaults, departments } = await CaseService.getSettings(
					Spark.state.currentTeam.id,
				);
				this.groups = groups || [];
				this.defaults = defaults || {};
				this.departments = departments || []; 
			} catch (error) {
				console.error('Error fetching settings:', error);
			}
		},

		async handleTabActivation(tabId, caseId) {
			// Reset form before anything to ensure a clean state
			this.resetForm();

			if (tabId === 'case-create') {
				await this.fetchSettings();

				if (new URLSearchParams(window.location.search).get('reviewId')) {
					this.review = await this.fetchReview(
						new URLSearchParams(window.location.search).get('reviewId'),
					);
				}

				if (new URLSearchParams(window.location.search).get('surveyResponseId')) {
					this.surveyResponse = await this.fetchSurveyResponse(
						new URLSearchParams(window.location.search).get('surveyResponseId'),
					);
				}

				
				if (caseId) {
					this.caseId = caseId;
					await this.fetchCaseDetails(caseId);
				} else {
					this.prefillForm({ ...this.form, ...this.defaults, review_id: this.review?.id, survey_response_id: this.surveyResponse?.id });
				}
			}
		},

		async fetchReview(reviewId) {
			try {
				const review = await ReviewsService.index(Spark.state.currentTeam.id, {
					id: reviewId,
				});

				return review?.data?.[0];
			} catch (error) {
				console.error('Error fetching review:', error);
			}
		},

		async fetchSurveyResponse(responseId) {
			try {
				const surveyResponse = await SurveysService.responses.index(1,{
					response_id: responseId,
				});
				return surveyResponse?.data?.[0];
			} catch (error) {
				console.error('Error fetching survey:', error);
			}
		},

		async fetchCaseDetails(caseId) {
			try {
				const caseData = await CaseService.getCaseDetails(
					Spark.state.currentTeam.id,
					caseId,
				);
				this.prefillForm(caseData);
			} catch (error) {
				console.error('Error fetching case details:', error);
			}
		},

		prefillForm(caseData) {
			this.form = {
				type: caseData.type || 'task',
				title: caseData.title || '',
				description: caseData.description || '',
				priority: caseData.priority || '1',
				status: caseData.status || 'new',
				assignees: this.transformAssignees(caseData.assignees || []),
				departments: caseData.departments || [],
				review_id: caseData.review_id || null,
				survey_response_id: caseData.survey_response_id || null,
			};

			if (caseData.review) {
				this.review = caseData.review;
			}

			if (caseData.surveyResponse) {
				this.surveyResponse = caseData.surveyResponse;
			}
		},

		transformAssignees(assignees) {
			return assignees
				.map((assignee) => {
					if (assignee.type === 'group' || assignee.group_id) {
						const group = this.groups.find((g) => g.id === assignee.group_id);
						return group
							? { key: { type: 'group', value: group.id }, label: group.name }
							: null;
					} else if (assignee.type === 'user' || assignee.user_id) {
						const user = Spark.state.currentTeam.users.find(
							(u) => u.id === assignee.user_id,
						);
						return user
							? {
								key: { type: 'user', value: user.id },
								label: `${user.name} (${user.email})`,
							}
							: null;
					} else if (assignee.type === 'email' || assignee.email) {
						return {
							key: { type: 'email', value: assignee.email },
							label: assignee.email,
						};
					}
					return null;
				})
				.filter(Boolean);
		},

		async submitForm() {
			try {
				const transformedData = this.transformPayload(this.form);
				if (this.caseId) {
					await CaseService.updateCase(
						Spark.state.currentTeam.id,
						this.caseId,
						transformedData,
					);
				} else {
					await CaseService.createCase(Spark.state.currentTeam.id, transformedData);
				}
				this.resetForm();
				this.$refs.assigneesSelector.reset();
				this.$refs.departmentSelector.reset();
				this.navigateToTab('cases-view');
			} catch (error) {
				if (error.errors) {
					this.formErrors = error.errors;
				}
			}
		},

		navigateToTab(tab, data) {
			this.$eventHub.$emit('activateTab', tab, data);
			$(this.$el).closest('div[role="tabcard"]').removeClass('active');
			$(`#${tab}`).addClass('active');
		},

		transformPayload(form) {
			return {
				...form,
				assignees: form.assignees.map((assignee) => {
					const { key } = assignee;
					if (key.type === 'user') return { user_id: key.value };
					if (key.type === 'group') return { group_id: key.value };
					if (key.type === 'email') return { email: key.value };
					return null;
				}).filter(Boolean),
				departments: form.departments.map(department => department.key),
			};
		},

		resetForm() {
			this.form = {
				type: 'task',
				title: '',
				description: '',
				assignees: [],
				departments: [],
				priority: '1',
				status: 'new',
				review_id: null,
				survey_response_id: null,
			};
			this.formErrors = {};
			this.caseId = null;
			this.review = null;
			this.surveyResponse = null;
		},

		setStatusMessage(message, type) {
			this.statusMessage = { message, type };
			setTimeout(() => {
				this.statusMessage = { message: '', type: 'success' };
			}, 5000);
		},
	},
};
</script>

<style scoped>
textarea#description {
	min-height: 100px;
}

.has-error .form-control {
	border-color: #dc3545;
}
</style>
